import { useState, useRef, useMemo, useEffect, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CheckCircle,
  Loader2,
  CalendarClock,
  Leaf,
  Play,
  BookOpen,
  Lightbulb,
  Palette,
  Gamepad2,
  Dumbbell,
  Music,
  Globe,
  PartyPopper,
  ChevronDown,
  ClipboardCheck,
  Palmtree,
  Undo2,
  type LucideIcon,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { cn, darkenColor, lightenColor, chipStyle } from '@/lib/utils'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useEtudiantByUserId } from '@/features/etudiants/hooks/use-etudiants'
import { useAgendaComplet, useTerminerTache, useTerminerTacheLangue, useAnnulerTache, useAnnulerTacheLangue, useReporterTache, useRecupererTache } from '@/features/etudiants/hooks/use-agenda'
import { WeekSelector } from '../components/week-selector'
import { useTypesActivite } from '@/features/types-activite/hooks/use-types-activite'
import { useMyEvents } from '@/features/events/hooks/use-events'
import { useDarkMode } from '@/shared/hooks/use-dark-mode'
import { agendaApi } from '@/features/etudiants/api/agenda.api'
import { BottomNav } from '../components/bottom-nav'
import type { TacheAgenda, TacheLangue, AgendaCompletDto } from '@/shared/types/agenda'
import type { ActivityResponseDto } from '@/shared/types/activite'

// ── Reward Badge (discret) ───────────────────────────────

const PALIERS = [
  { seuil: 0, emoji: '🥉', couleur: '#CD7F32', bg: '#fef3c7' },
  { seuil: 25, emoji: '🥈', couleur: '#C0C0C0', bg: '#f1f5f9' },
  { seuil: 50, emoji: '🥇', couleur: '#FFD700', bg: '#fef3c7' },
  { seuil: 100, emoji: '💎', couleur: '#60a5fa', bg: '#eff6ff' },
  { seuil: 200, emoji: '👑', couleur: '#a855f7', bg: '#faf5ff' },
] as const

function RewardBadge({ totalTachesTerminees }: { totalTachesTerminees: number }) {
  let current: (typeof PALIERS)[number] = PALIERS[0]
  for (const p of PALIERS) {
    if (totalTachesTerminees >= p.seuil) current = p
  }
  return (
    <div
      className="mx-auto mb-0.5 flex w-fit items-center gap-1 rounded-full px-2 py-0.5"
      style={{ backgroundColor: current.bg }}
    >
      <span className="text-sm">{current.emoji}</span>
      <span className="text-xs font-bold" style={{ color: current.couleur }}>{totalTachesTerminees}</span>
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────

function isToday(date: Date): boolean {
  const now = new Date()
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

function formatDateFull(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

function formatDateApi(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ── Section building ─────────────────────────────────────

const SECTION_ICON_MAP: Record<string, LucideIcon> = {
  rev: BookOpen,
  révis: BookOpen,
  revis: BookOpen,
  culture: Lightbulb,
  loisir: Gamepad2,
  sport: Dumbbell,
  musique: Music,
  art: Palette,
  langue: Globe,
}

function getSectionIcon(code: string, libelle: string): LucideIcon {
  const search = `${code} ${libelle}`.toLowerCase()
  for (const [keyword, icon] of Object.entries(SECTION_ICON_MAP)) {
    if (search.includes(keyword)) return icon
  }
  return BookOpen
}

interface DaySection {
  key: string
  title: string
  color: string
  icon: LucideIcon
  timeSlots: string[]
  taches: TacheAgenda[]
  tachesLangue: TacheLangue[]
  activites: ActivityResponseDto[]
}

function buildSections(agenda: AgendaCompletDto, revisionColor: string | undefined, t: (key: string) => string): DaySection[] {
  const sections: DaySection[] = []

  // Group activities by typeActivite.id
  const groups = new Map<number, { type: ActivityResponseDto['typeActivite']; items: ActivityResponseDto[] }>()
  for (const act of agenda.activites) {
    const tid = act.typeActivite.id
    if (!groups.has(tid)) groups.set(tid, { type: act.typeActivite, items: [] })
    groups.get(tid)!.items.push(act)
  }

  // Detect revision-type activities (for study task time slots)
  let revTypeId: number | null = null
  for (const [tid, g] of groups) {
    const c = g.type.code.toLowerCase()
    const l = g.type.libelle.toLowerCase()
    if (c.includes('rev') || l.includes('révis') || l.includes('revis')) {
      revTypeId = tid
      break
    }
  }

  // Séparer tâches quotidiennes (planning) et tâches événements
  const planningTasks = agenda.taches.filter((t) => t.isFromPlanning)
  const controleTasks = agenda.taches.filter((t) => !t.isFromPlanning && t.eventOrigin === 'CONTROLE')
  const vacancesTasks = agenda.taches.filter((t) => !t.isFromPlanning && t.eventOrigin === 'VACANCES')
  const otherEventTasks = agenda.taches.filter((t) => !t.isFromPlanning && !t.eventOrigin)

  // Révisions quotidiennes (issues du planning)
  if (planningTasks.length > 0 || otherEventTasks.length > 0) {
    const rev = revTypeId !== null ? groups.get(revTypeId) : undefined
    sections.push({
      key: 'revisions',
      title: rev?.type.libelle ?? t('mobile.dashboard.revisions'),
      color: rev?.type.couleur ?? revisionColor ?? '#FF6B6B',
      icon: rev ? getSectionIcon(rev.type.code, rev.type.libelle) : BookOpen,
      timeSlots: rev?.items.map((a) => `${a.startTime} - ${a.endTime}`) ?? [],
      taches: [...planningTasks, ...otherEventTasks].sort((a, b) => a.action.id - b.action.id),
      tachesLangue: [],
      activites: [],
    })
  }

  // Préparation contrôle
  if (controleTasks.length > 0) {
    sections.push({
      key: 'revisions-controle',
      title: t('mobile.dashboard.testPrep'),
      color: '#F59E0B',
      icon: ClipboardCheck,
      timeSlots: [],
      taches: [...controleTasks].sort((a, b) => a.action.id - b.action.id),
      tachesLangue: [],
      activites: [],
    })
  }

  // Révisions de vacances
  if (vacancesTasks.length > 0) {
    sections.push({
      key: 'revisions-vacances',
      title: t('mobile.dashboard.vacationRevisions'),
      color: '#22C55E',
      icon: Palmtree,
      timeSlots: [],
      taches: [...vacancesTasks].sort((a, b) => a.action.id - b.action.id),
      tachesLangue: [],
      activites: [],
    })
  }

  // Langues section (language tasks)
  if (agenda.tachesLangue.length > 0) {
    const langueColor = '#22C55E'
    sections.push({
      key: 'langues',
      title: t('mobile.dashboard.languages'),
      color: langueColor,
      icon: Globe,
      timeSlots: [],
      taches: [],
      tachesLangue: agenda.tachesLangue,
      activites: [],
    })
  }

  // Tâches reportées (visible uniquement pour les jours != aujourd'hui)
  if (agenda.tachesReportees && agenda.tachesReportees.length > 0) {
    sections.push({
      key: 'taches-reportees',
      title: t('mobile.dashboard.postponedTasks'),
      color: '#F59E0B',
      icon: CalendarClock,
      timeSlots: [],
      taches: agenda.tachesReportees,
      tachesLangue: [],
      activites: [],
    })
  }

  // Other activity type sections
  for (const [tid, g] of groups) {
    if (tid === revTypeId && (planningTasks.length > 0 || otherEventTasks.length > 0)) continue
    sections.push({
      key: `type-${tid}`,
      title: g.type.libelle,
      color: g.type.couleur ?? '#6B7280',
      icon: getSectionIcon(g.type.code, g.type.libelle),
      timeSlots: [],
      taches: [],
      tachesLangue: [],
      activites: g.items,
    })
  }

  return sections
}

// ── Swipeable Row (swipe left = done, swipe right = postpone) ─

function SwipeableRow({
  children,
  onSwipeLeft,
  onSwipeRight,
  disabled,
  leftLabel,
  rightLabel,
  leftBgRgb = '16, 185, 129',
  leftTextClass = 'text-emerald-600',
  leftIconClass = 'text-emerald-500',
  LeftIcon = CheckCircle,
}: {
  children: ReactNode
  onSwipeLeft: () => void
  onSwipeRight?: () => void
  disabled?: boolean
  leftLabel: string
  rightLabel?: string
  leftBgRgb?: string
  leftTextClass?: string
  leftIconClass?: string
  LeftIcon?: LucideIcon
}) {
  const [translateX, setTranslateX] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const startXRef = useRef(0)
  const startYRef = useRef(0)
  const isHorizontalRef = useRef<boolean | null>(null)
  const didSwipeRef = useRef(false)

  const THRESHOLD = 100

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled) return
    startXRef.current = e.touches[0].clientX
    startYRef.current = e.touches[0].clientY
    isHorizontalRef.current = null
    didSwipeRef.current = false
    setIsSwiping(true)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (disabled || !isSwiping) return
    const dx = e.touches[0].clientX - startXRef.current
    const dy = e.touches[0].clientY - startYRef.current

    if (isHorizontalRef.current === null) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        isHorizontalRef.current = Math.abs(dx) > Math.abs(dy)
      }
      return
    }

    if (!isHorizontalRef.current) return

    didSwipeRef.current = true
    const maxRight = onSwipeRight ? THRESHOLD + 30 : 0
    const clamped = Math.max(-(THRESHOLD + 30), Math.min(dx * 0.8, maxRight))
    setTranslateX(clamped)
  }

  const handleTouchEnd = () => {
    if (disabled) return
    setIsSwiping(false)
    if (translateX <= -THRESHOLD) {
      onSwipeLeft()
    } else if (translateX >= THRESHOLD && onSwipeRight) {
      onSwipeRight()
    }
    setTranslateX(0)
    isHorizontalRef.current = null
    setTimeout(() => {
      didSwipeRef.current = false
    }, 100)
  }

  const handleClickCapture = (e: React.MouseEvent) => {
    if (didSwipeRef.current) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  const leftProgress = Math.min(1, Math.max(0, -translateX / THRESHOLD))
  const rightProgress = Math.min(1, Math.max(0, translateX / THRESHOLD))

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Left swipe background */}
      <div
        className="absolute inset-0 flex items-center justify-end rounded-xl pr-5"
        style={{
          backgroundColor: `rgba(${leftBgRgb}, ${0.08 + leftProgress * 0.15})`,
          opacity: leftProgress,
        }}
      >
        <div className="flex items-center gap-2">
          <span className={cn('text-sm font-bold', leftTextClass)}>{leftLabel}</span>
          <LeftIcon
            className={cn('h-6 w-6', leftIconClass)}
            style={{ transform: `scale(${0.5 + leftProgress * 0.5})` }}
          />
        </div>
      </div>

      {/* Right swipe background: amber "Reporter" (only if onSwipeRight is defined) */}
      {onSwipeRight && (
        <div
          className="absolute inset-0 flex items-center rounded-xl pl-5"
          style={{
            backgroundColor: `rgba(245, 158, 11, ${0.08 + rightProgress * 0.12})`,
            opacity: rightProgress,
          }}
        >
          <div className="flex items-center gap-2">
            <CalendarClock
              className="h-6 w-6 text-amber-500"
              style={{ transform: `scale(${0.5 + rightProgress * 0.5})` }}
            />
            <span className="text-sm font-bold text-amber-600">{rightLabel}</span>
          </div>
        </div>
      )}

      {/* Foreground (swipeable content) */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClickCapture={handleClickCapture}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
        }}
      >
        {children}
      </div>
    </div>
  )
}

// ── Section Header ───────────────────────────────────────

function SectionHeader({
  title, color, icon: Icon, timeSlots, collapsed, onToggle, taskCount, isDark,
}: {
  title: string; color: string; icon: LucideIcon; timeSlots: string[]
  collapsed: boolean; onToggle: () => void; taskCount: number; isDark: boolean
}) {
  const dark = isDark ? lightenColor(color) : darkenColor(color)
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full flex-wrap items-center gap-2 px-1 pb-2.5"
    >
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: color + '20' }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color: dark }} />
      </div>
      <h2 className="text-xs font-bold uppercase tracking-wider" style={{ color: dark }}>
        {title}
      </h2>
      {timeSlots.map((slot, i) => (
        <span
          key={i}
          className="rounded-full px-2.5 py-0.5 text-[11px] font-medium"
          style={{ backgroundColor: color + '15', color: dark }}
        >
          {slot}
        </span>
      ))}
      <span className="ml-auto flex items-center gap-1">
        {collapsed && (
          <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: color + '20', color: dark }}>
            {taskCount}
          </span>
        )}
        <ChevronDown
          className="h-4 w-4 transition-transform"
          style={{ color: dark, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
        />
      </span>
    </button>
  )
}

// ── Task Cards ───────────────────────────────────────────

function StudyTaskCard({
  tache,
  todaySelected,
  onNavigateTimer,
  sectionColor,
  isDark,
}: {
  tache: TacheAgenda
  todaySelected: boolean
  onNavigateTimer: () => void
  sectionColor: string
  isDark: boolean
}) {
  const { t } = useTranslation()
  const accentColor = tache.action.couleur || sectionColor
  const totalSeconds = tache.action.dureeMinutes * 60
  const started = tache.timerSeconds !== null && tache.timerSeconds < totalSeconds
  const progress = started ? Math.round(((totalSeconds - tache.timerSeconds!) / totalSeconds) * 100) : 0

  return (
    <div
      onClick={() => {
        if (todaySelected && !tache.termine && tache.action.dureeMinutes > 0) onNavigateTimer()
      }}
      className={cn(
        'relative overflow-hidden rounded-xl bg-white',
        tache.termine ? 'opacity-70' : 'shadow-sm',
        todaySelected && !tache.termine && tache.action.dureeMinutes > 0 && 'cursor-pointer active:scale-[0.98]',
      )}
    >
      {/* Left accent bar — color from action */}
      <div
        className="absolute inset-y-0 left-0 w-1 rounded-l-xl"
        style={{ backgroundColor: tache.termine ? '#10b981' : accentColor }}
      />

      <div className="p-3 pl-4">
        {/* Title row */}
        <div className="flex items-center justify-between gap-2">
          <h3 className={cn('text-sm font-bold', tache.termine ? 'text-neutral-400' : 'text-neutral-900')}>
            {tache.action.libelle}
          </h3>
          {tache.termine ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-500">
              <CheckCircle className="h-3.5 w-3.5" />
              {t('mobile.dashboard.completed')}
            </span>
          ) : (
            <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-500">
              {tache.action.dureeMinutes} min
            </span>
          )}
        </div>

        {/* Subject chips — matière colors */}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {tache.matieres.map((m, i) => {
            const c = m.matiereCouleur || sectionColor
            return (
              <span
                key={i}
                className="rounded-lg px-2.5 py-0.5 text-xs font-semibold"
                style={chipStyle(c, isDark)}
              >
                {m.matiereNom}
              </span>
            )
          })}
        </div>

        {/* Progress bar — visible when started but not finished */}
        {!tache.termine && started && (
          <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${progress}%`, backgroundColor: accentColor }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function LangueTaskCard({
  tache,
  sectionColor,
  isDark,
}: {
  tache: TacheLangue
  sectionColor: string
  isDark: boolean
}) {
  const { t } = useTranslation()
  const c = tache.matiereCouleur || sectionColor

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl bg-white',
        tache.termine ? 'opacity-70' : 'shadow-sm',
      )}
    >
      <div
        className="absolute inset-y-0 left-0 w-1 rounded-l-xl"
        style={{ backgroundColor: tache.termine ? '#10b981' : c }}
      />

      <div className="p-3 pl-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className={cn('text-sm font-bold', tache.termine ? 'text-neutral-400' : 'text-neutral-900')}>
            {tache.libelle}
          </h3>
          {tache.termine && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-500">
              <CheckCircle className="h-3.5 w-3.5" />
              {t('mobile.dashboard.completed')}
            </span>
          )}
        </div>
        <div className="mt-1.5">
          <span
            className="rounded-lg px-2.5 py-0.5 text-xs font-semibold"
            style={chipStyle(c, isDark)}
          >
            {tache.matiereNom}
          </span>
        </div>
      </div>
    </div>
  )
}

function ActivityItemCard({
  activity,
  todaySelected,
  onNavigateTimer,
}: {
  activity: ActivityResponseDto
  todaySelected: boolean
  onNavigateTimer?: () => void
}) {
  const { t } = useTranslation()
  const c = activity.typeActivite.couleur ?? '#6B7280'
  const totalSeconds = activity.dureeMinutes * 60
  const started = activity.timerSeconds !== null && activity.timerSeconds < totalSeconds
  const progress = started ? Math.round(((totalSeconds - activity.timerSeconds!) / totalSeconds) * 100) : 0

  return (
    <div
      onClick={() => {
        if (todaySelected && !activity.termine && activity.dureeMinutes > 0 && onNavigateTimer) onNavigateTimer()
      }}
      className={cn(
        'relative overflow-hidden rounded-xl bg-white',
        activity.termine ? 'opacity-70' : 'shadow-sm',
        todaySelected && !activity.termine && activity.dureeMinutes > 0 && 'cursor-pointer active:scale-[0.98]',
      )}
    >
      <div className="absolute inset-y-0 left-0 w-1 rounded-l-xl" style={{ backgroundColor: activity.termine ? '#10b981' : c }} />
      <div className="p-3 pl-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className={cn('text-sm font-bold', activity.termine ? 'text-neutral-400' : 'text-neutral-900')}>{activity.title}</h3>
          {activity.termine ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-500">
              <CheckCircle className="h-3.5 w-3.5" />
              {t('mobile.dashboard.completed')}
            </span>
          ) : (
            <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-500">
              {activity.dureeMinutes} min
            </span>
          )}
        </div>

        {/* Progress bar */}
        {!activity.termine && started && (
          <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${progress}%`, backgroundColor: c }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Scan Required Card ───────────────────────────────────

function ScanRequiredCard({ onStart }: { onStart: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center rounded-2xl bg-gradient-to-b from-emerald-50/80 to-white px-6 py-10">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
        <Leaf className="h-8 w-8 text-emerald-500" />
      </div>
      <h2 className="mt-4 text-lg font-bold text-neutral-800">
        {t('mobile.dashboard.scanRequired')}
      </h2>
      <p className="mt-2 max-w-[260px] text-center text-sm leading-relaxed text-neutral-400">
        {t('mobile.dashboard.scanRequiredSubtitle')}
      </p>
      <button
        onClick={onStart}
        className="mt-6 flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-500/25 transition-transform active:scale-95"
      >
        <Play className="h-4 w-4" />
        {t('mobile.dashboard.startScan')}
      </button>
    </div>
  )
}

// ── Empty State ──────────────────────────────────────────

function EmptyDay() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center rounded-2xl border-2 border-dashed border-neutral-200 px-6 py-10">
      <span className="text-3xl">📋</span>
      <p className="mt-2 text-sm text-neutral-400">{t('mobile.dashboard.emptyDay')}</p>
    </div>
  )
}

// ── Relax Day Card ───────────────────────────────────────

function RelaxDayCard({ libelle }: { libelle: string | null }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center rounded-2xl bg-gradient-to-b from-amber-50/80 to-white px-6 py-10">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
        <PartyPopper className="h-8 w-8 text-amber-500" strokeWidth={1.8} />
      </div>
      <h2 className="mt-4 text-lg font-bold text-neutral-800">
        {libelle || t('mobile.dashboard.relaxDay')}
      </h2>
      <p className="mt-2 max-w-[260px] text-center text-sm leading-relaxed text-neutral-400">
        {t('mobile.dashboard.relaxDayMessage')}
      </p>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────

export function DashboardMobilePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const { data: etudiant } = useEtudiantByUserId(user?.id)
  const { isDark } = useDarkMode()
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US'

  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const todaySelected = isToday(selectedDate)

  const { data: agenda, isLoading, isFetching } = useAgendaComplet(etudiant?.id, selectedDate)
  const { data: typesActivite } = useTypesActivite()
  const { data: myEvents } = useMyEvents()

  const revisionColor = useMemo(() => {
    if (!typesActivite) return undefined
    const rev = typesActivite.find((t) => t.code.toLowerCase().includes('rev'))
    return rev?.couleur
  }, [typesActivite])

  const relaxEvent = useMemo(() => {
    if (!myEvents) return undefined
    const dateStr = formatDateApi(selectedDate)
    return myEvents.find((e) => {
      if (e.type !== 'RELAX') return false
      if (e.dateFin) return dateStr >= e.dateDebut && dateStr <= e.dateFin
      return e.dateDebut === dateStr
    })
  }, [myEvents, selectedDate])

  const controleEvent = useMemo(() => {
    if (!myEvents) return undefined
    const dateStr = formatDateApi(selectedDate)
    return myEvents.find((e) => e.type === 'CONTROLE' && e.dateDebut === dateStr)
  }, [myEvents, selectedDate])

  const terminerTache = useTerminerTache(etudiant?.id ?? 0, selectedDate)
  const terminerLangue = useTerminerTacheLangue(etudiant?.id ?? 0, selectedDate)
  const annulerTache = useAnnulerTache(etudiant?.id ?? 0, selectedDate)
  const annulerLangue = useAnnulerTacheLangue(etudiant?.id ?? 0, selectedDate)
  const reporterTache = useReporterTache(etudiant?.id ?? 0, selectedDate)
  const recupererTache = useRecupererTache(etudiant?.id ?? 0, selectedDate)

  // Store timer values before completing, so we can restore them on undo
  const savedTimersRef = useRef<Record<number, number>>({})

  const terminerActivity = useMutation({
    mutationFn: (actId: number) => agendaApi.terminerActivity(actId, selectedDate),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agenda'] }),
  })

  const annulerActivity = useMutation({
    mutationFn: (actId: number) => agendaApi.annulerActivity(actId, selectedDate),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agenda'] }),
  })

  // Scan pending: student must do scan before accessing tasks
  const scanPending = todaySelected && agenda?.tacheScan && !agenda.tacheScan.termine

  // Auto-redirect to /scan on first load if scan is pending
  // - Skip if user came back from scan via "Plus tard" (fromScan=1)
  // - Skip while refetching: stale cache may still show termine=false
  const fromScan = searchParams.get('fromScan') === '1'
  useEffect(() => {
    if (scanPending && !fromScan && !isFetching && agenda?.tacheScan) {
      const scan = agenda.tacheScan
      const timer = scan.timerSeconds ?? scan.action.dureeMinutes * 60
      navigate(`/scan?duree=${scan.action.dureeMinutes}&tacheAgendaId=${scan.id}&timerSeconds=${timer}`, { replace: true })
    }
  }, [scanPending, fromScan, isFetching, agenda, navigate])

  const sections = useMemo(() => {
    if (!agenda) return []
    return buildSections(agenda, revisionColor, t)
  }, [agenda, revisionColor, t])

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const isEmpty = !agenda || sections.length === 0

  const handleNavigateTimer = (tache: TacheAgenda) => {
    const matieres = tache.matieres.map((m) => m.matiereNom).join(',')
    const couleurs = tache.matieres.map((m) => m.matiereCouleur || '#79305D').join(',')
    const actionCouleur = tache.action.couleur || '#BBDEFB'
    const timer = tache.timerSeconds ?? tache.action.dureeMinutes * 60
    navigate(`/action-timer?action=${encodeURIComponent(tache.action.libelle)}&duree=${tache.action.dureeMinutes}&actionCouleur=${encodeURIComponent(actionCouleur)}&matieres=${encodeURIComponent(matieres)}&couleurs=${encodeURIComponent(couleurs)}&timerSeconds=${timer}&tacheAgendaId=${tache.id}`)
  }

  const handleNavigateActivityTimer = (act: ActivityResponseDto) => {
    const c = act.typeActivite.couleur ?? '#6B7280'
    const timer = act.timerSeconds ?? act.dureeMinutes * 60
    navigate(`/action-timer?action=${encodeURIComponent(act.title)}&duree=${act.dureeMinutes}&actionCouleur=${encodeURIComponent(c)}&timerSeconds=${timer}&activityId=${act.id}`)
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-gradient-to-b from-primary/5 via-primary/[0.02] to-white">
      {/* Sticky header */}
      <div className="sticky top-0 z-40 border-b border-neutral-100 bg-white/90 pb-3 pt-3 backdrop-blur-md">
        {agenda && <RewardBadge totalTachesTerminees={agenda.totalTachesTerminees} />}
        <h1 className="px-4 text-center text-lg font-bold capitalize text-neutral-900">
          {formatDateFull(selectedDate, locale)}
        </h1>
        <div className="mt-2">
          <WeekSelector selectedDate={selectedDate} onDateChange={setSelectedDate} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-24 pt-4">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : relaxEvent ? (
          <RelaxDayCard libelle={relaxEvent.libelle} />
        ) : isEmpty && !controleEvent ? (
          <EmptyDay />
        ) : (
          <div className="space-y-4">
            {scanPending && (
              <ScanRequiredCard
                onStart={() => {
                  const scan = agenda!.tacheScan!
                  const timer = scan.timerSeconds ?? scan.action.dureeMinutes * 60
                  navigate(`/scan?duree=${scan.action.dureeMinutes}&tacheAgendaId=${scan.id}&timerSeconds=${timer}`)
                }}
              />
            )}
            {controleEvent && (
              <div
                className="overflow-hidden rounded-2xl"
                style={{ backgroundColor: (controleEvent.matiereCouleur || '#EF4444') + '12' }}
              >
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: (controleEvent.matiereCouleur || '#EF4444') + '25' }}
                  >
                    <ClipboardCheck className="h-5 w-5" style={{ color: controleEvent.matiereCouleur || '#EF4444' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-neutral-800">
                      {controleEvent.matiereNom ? t('mobile.dashboard.testOf', { subject: controleEvent.matiereNom }) : t('mobile.dashboard.test')}
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-400">
                      {t('mobile.dashboard.testEncouragement')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {sections.map((section) => {
              const isCollapsed = collapsedSections.has(section.key)
              const taskCount = section.taches.length + section.tachesLangue.length + section.activites.length
              return (
                <div
                  key={section.key}
                  className="rounded-2xl p-3"
                  style={{ backgroundColor: section.color + '0D' }}
                >
                  <SectionHeader
                    title={section.title}
                    color={section.color}
                    icon={section.icon}
                    timeSlots={section.timeSlots}
                    collapsed={isCollapsed}
                    onToggle={() => toggleSection(section.key)}
                    taskCount={taskCount}
                    isDark={isDark}
                  />
                  {!isCollapsed && (
                    <div className="space-y-2">
                      {/* Study tasks */}
                      {section.taches.map((tache) => {
                        const isReportedSection = section.key === 'taches-reportees'

                        // Tâches reportées : swipe gauche = récupérer (bleu)
                        if (isReportedSection && !todaySelected) {
                          return (
                            <SwipeableRow
                              key={tache.id}
                              onSwipeLeft={() => recupererTache.mutate(tache.id)}
                              leftLabel={t('mobile.dashboard.recover')}
                              leftBgRgb="59, 130, 246"
                              leftTextClass="text-blue-600"
                              leftIconClass="text-blue-500"
                              LeftIcon={Undo2}
                            >
                              <StudyTaskCard
                                tache={tache}
                                todaySelected={false}
                                onNavigateTimer={() => handleNavigateTimer(tache)}
                                sectionColor={section.color}
                                isDark={isDark}
                              />
                            </SwipeableRow>
                          )
                        }

                        // Tâches normales aujourd'hui : swipe gauche = terminé, swipe droit = reporter
                        if (todaySelected && !tache.termine) {
                          return (
                            <SwipeableRow
                              key={tache.id}
                              onSwipeLeft={() => {
                                if (tache.timerSeconds) savedTimersRef.current[tache.id] = tache.timerSeconds
                                terminerTache.mutate(tache.id)
                              }}
                              onSwipeRight={tache.nbJoursReportRestant > 0 ? () => reporterTache.mutate(tache.id) : undefined}
                              leftLabel={t('mobile.dashboard.completed')}
                              rightLabel={t('mobile.dashboard.postpone')}
                            >
                              <StudyTaskCard
                                tache={tache}
                                todaySelected={todaySelected}
                                onNavigateTimer={() => handleNavigateTimer(tache)}
                                sectionColor={section.color}
                                isDark={isDark}
                              />
                            </SwipeableRow>
                          )
                        }

                        // Tâche terminée aujourd'hui : swipe gauche = annuler
                        if (todaySelected && tache.termine) {
                          return (
                            <SwipeableRow
                              key={tache.id}
                              onSwipeLeft={() => annulerTache.mutate({ tacheId: tache.id, timerSeconds: savedTimersRef.current[tache.id] })}
                              leftLabel={t('mobile.dashboard.undo', 'Reprendre')}
                              leftBgRgb="239, 68, 68"
                              leftTextClass="text-red-600"
                              leftIconClass="text-red-500"
                              LeftIcon={Undo2}
                            >
                              <StudyTaskCard
                                tache={tache}
                                todaySelected={todaySelected}
                                onNavigateTimer={() => handleNavigateTimer(tache)}
                                sectionColor={section.color}
                                isDark={isDark}
                              />
                            </SwipeableRow>
                          )
                        }

                        // Pas de swipe (jour futur/passé)
                        return (
                          <StudyTaskCard
                            key={tache.id}
                            tache={tache}
                            todaySelected={todaySelected}
                            onNavigateTimer={() => handleNavigateTimer(tache)}
                            sectionColor={section.color}
                            isDark={isDark}
                          />
                        )
                      })}

                      {/* Language tasks */}
                      {section.tachesLangue.map((tache) =>
                        todaySelected && !tache.termine ? (
                          <SwipeableRow
                            key={tache.id}
                            onSwipeLeft={() => {
                              if (tache.timerSeconds) savedTimersRef.current[tache.id] = tache.timerSeconds
                              terminerLangue.mutate(tache.id)
                            }}
                            onSwipeRight={undefined}
                            leftLabel={t('mobile.dashboard.completed')}
                          >
                            <LangueTaskCard tache={tache} sectionColor={section.color} isDark={isDark} />
                          </SwipeableRow>
                        ) : todaySelected && tache.termine ? (
                          <SwipeableRow
                            key={tache.id}
                            onSwipeLeft={() => annulerLangue.mutate({ tacheLangueId: tache.id, timerSeconds: savedTimersRef.current[tache.id] })}
                            leftLabel={t('mobile.dashboard.undo', 'Reprendre')}
                            leftBgRgb="239, 68, 68"
                            leftTextClass="text-red-600"
                            leftIconClass="text-red-500"
                            LeftIcon={Undo2}
                          >
                            <LangueTaskCard tache={tache} sectionColor={section.color} isDark={isDark} />
                          </SwipeableRow>
                        ) : (
                          <LangueTaskCard key={tache.id} tache={tache} sectionColor={section.color} isDark={isDark} />
                        ),
                      )}

                      {/* Activities */}
                      {section.activites.map((act) => {
                        const navigateTimer = undefined
                        return todaySelected && !act.termine ? (
                          <SwipeableRow
                            key={act.id}
                            onSwipeLeft={() => terminerActivity.mutate(act.id)}
                            leftLabel={t('mobile.dashboard.completed')}
                          >
                            <ActivityItemCard
                              activity={act}
                              todaySelected={todaySelected}
                              onNavigateTimer={navigateTimer}
                            />
                          </SwipeableRow>
                        ) : todaySelected && act.termine ? (
                          <SwipeableRow
                            key={act.id}
                            onSwipeLeft={() => annulerActivity.mutate(act.id)}
                            leftLabel={t('mobile.dashboard.undo', 'Reprendre')}
                            leftBgRgb="239, 68, 68"
                            leftTextClass="text-red-600"
                            leftIconClass="text-red-500"
                            LeftIcon={Undo2}
                          >
                            <ActivityItemCard
                              activity={act}
                              todaySelected={todaySelected}
                              onNavigateTimer={navigateTimer}
                            />
                          </SwipeableRow>
                        ) : (
                          <ActivityItemCard
                            key={act.id}
                            activity={act}
                            todaySelected={todaySelected}
                            onNavigateTimer={navigateTimer}
                          />
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <BottomNav />

    </div>
  )
}
